import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Target, Trash2, Edit2, TrendingUp, CalendarDays, Sparkles,
  ArrowRight, AlertTriangle, GraduationCap, ClipboardList, CheckCircle2
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
} from 'recharts';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { CollegeLogo } from '../components/CollegeSearchCombobox';
import { useAuth } from '../context/AuthContext';
import { format, parseISO, differenceInCalendarDays } from 'date-fns';
import { toast } from 'sonner';

const TEST_TYPE_STYLES = {
  DIAGNOSTIC: 'bg-amber-50 text-amber-700 border-amber-200',
  PRACTICE: 'bg-blue-50 text-blue-700 border-blue-200',
  OFFICIAL: 'bg-green-50 text-green-700 border-green-200',
};
const TEST_TYPE_LABELS = { DIAGNOSTIC: 'Diagnostic', PRACTICE: 'Practice', OFFICIAL: 'Official' };
const TEST_TYPES = ['DIAGNOSTIC', 'PRACTICE', 'OFFICIAL'];

const SAT_SECTION_LABELS = {
  READING_WRITING: 'Reading & Writing',
  MATH: 'Math',
  FULL_TEST: 'Full Test',
  GENERAL: 'General',
};

const today = () => format(new Date(), 'yyyy-MM-dd');
const emptyGoal = { targetTotal: '', targetReadingWrite: '', targetMath: '', targetTestDate: '' };
const emptyTest = { takenOn: today(), testType: 'PRACTICE', source: '', readingWrite: '', math: '', notes: '' };

// Scale used for the target-college SAT range bars
const SCALE_MIN = 800;
const SCALE_MAX = 1600;
const scalePct = (v) => Math.max(0, Math.min(100, ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));

export default function SatPrepPage() {
  const { api } = useAuth();
  const [plan, setPlan] = useState(null);
  const [tests, setTests] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [clusterWarnings, setClusterWarnings] = useState([]);
  const [testDates, setTestDates] = useState([]);
  const [satSessions, setSatSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showGoal, setShowGoal] = useState(false);
  const [goalForm, setGoalForm] = useState(emptyGoal);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const [showTest, setShowTest] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [testForm, setTestForm] = useState(emptyTest);

  const fetchAll = useCallback(async () => {
    try {
      const [planRes, testsRes, collegesRes, deadlinesRes, datesRes, sessionsRes] = await Promise.all([
        api.get('/sat/plan'),
        api.get('/sat/tests'),
        api.get('/sat/college-targets'),
        api.get('/deadlines'),
        api.get('/sat/test-dates'),
        api.get('/work-sessions?satOnly=true'),
      ]);
      setPlan(planRes.data.plan || null);
      setTests(testsRes.data.tests || []);
      setColleges(collegesRes.data.colleges || []);
      setDeadlines(deadlinesRes.data.deadlines || []);
      setClusterWarnings(deadlinesRes.data.clusterWarnings || []);
      setTestDates(datesRes.data.dates || []);
      setSatSessions(sessionsRes.data.sessions || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load SAT prep data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- derived ----
  const latest = tests.length ? tests[tests.length - 1] : null;
  const best = useMemo(
    () => tests.reduce((m, t) => (m == null || t.total > m.total ? t : m), null),
    [tests]
  );
  const daysLeft = plan?.targetTestDate
    ? differenceInCalendarDays(parseISO(plan.targetTestDate), new Date())
    : null;

  const satDeadlines = useMemo(() => deadlines.filter((d) => d.category === 'SAT'), [deadlines]);
  const satDeadlineIds = useMemo(() => new Set(satDeadlines.map((d) => d.id)), [satDeadlines]);
  const satWarnings = useMemo(
    () => clusterWarnings.filter((w) => (w.deadlineIds || []).some((id) => satDeadlineIds.has(id))),
    [clusterWarnings, satDeadlineIds]
  );

  const chartData = useMemo(
    () => tests.map((t) => ({
      label: format(parseISO(t.takenOn), 'MMM d'),
      total: t.total,
      rw: t.readingWrite,
      math: t.math,
      type: t.testType,
    })),
    [tests]
  );

  const yDomain = useMemo(() => {
    const vals = tests.map((t) => t.total);
    if (plan?.targetTotal) vals.push(plan.targetTotal);
    if (!vals.length) return [800, 1600];
    const lo = Math.max(400, Math.floor((Math.min(...vals) - 80) / 50) * 50);
    const hi = Math.min(1600, Math.ceil((Math.max(...vals) + 80) / 50) * 50);
    return [lo, hi];
  }, [tests, plan]);

  // ---- goal ----
  const openGoal = () => {
    setSuggestion(null);
    setGoalForm({
      targetTotal: plan?.targetTotal ? String(plan.targetTotal) : '',
      targetReadingWrite: plan?.targetReadingWrite ? String(plan.targetReadingWrite) : '',
      targetMath: plan?.targetMath ? String(plan.targetMath) : '',
      targetTestDate: plan?.targetTestDate ? format(parseISO(plan.targetTestDate), 'yyyy-MM-dd') : '',
    });
    setShowGoal(true);
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const res = await api.get('/sat/target-suggestion');
      const s = res.data;
      setSuggestion(s);
      if (s.suggestedTotal == null) {
        toast.info(s.message || 'No SAT data available from your colleges yet');
      } else {
        setGoalForm((f) => ({
          ...f,
          targetTotal: String(s.suggestedTotal),
          targetReadingWrite: s.targetReadingWrite ? String(s.targetReadingWrite) : f.targetReadingWrite,
          targetMath: s.targetMath ? String(s.targetMath) : f.targetMath,
        }));
        toast.success('Target suggested from your colleges');
      }
    } catch {
      toast.error('Failed to get suggestion');
    } finally {
      setSuggesting(false);
    }
  };

  const saveGoal = async () => {
    const payload = {
      targetTotal: goalForm.targetTotal ? Number(goalForm.targetTotal) : null,
      targetReadingWrite: goalForm.targetReadingWrite ? Number(goalForm.targetReadingWrite) : null,
      targetMath: goalForm.targetMath ? Number(goalForm.targetMath) : null,
      targetTestDate: goalForm.targetTestDate || null,
      suggestedTotal: suggestion?.suggestedTotal ?? plan?.suggestedTotal ?? null,
      suggestionSource: suggestion
        ? `75th pct across ${suggestion.basis.length} target college(s)`
        : plan?.suggestionSource ?? null,
    };
    try {
      const res = await api.put('/sat/plan', payload);
      setPlan(res.data.plan);
      setShowGoal(false);
      toast.success('SAT goal saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save goal');
    }
  };

  // ---- tests ----
  const openCreateTest = () => {
    setEditingTest(null);
    setTestForm(emptyTest);
    setShowTest(true);
  };

  const openEditTest = (t) => {
    setEditingTest(t);
    setTestForm({
      takenOn: format(parseISO(t.takenOn), 'yyyy-MM-dd'),
      testType: t.testType,
      source: t.source || '',
      readingWrite: String(t.readingWrite),
      math: String(t.math),
      notes: t.notes || '',
    });
    setShowTest(true);
  };

  const saveTest = async () => {
    const rw = Number(testForm.readingWrite);
    const m = Number(testForm.math);
    if (!testForm.takenOn) return toast.error('Date is required');
    if (!Number.isInteger(rw) || rw < 200 || rw > 800) return toast.error('Reading & Writing must be 200–800');
    if (!Number.isInteger(m) || m < 200 || m > 800) return toast.error('Math must be 200–800');
    const payload = {
      takenOn: testForm.takenOn,
      testType: testForm.testType,
      source: testForm.source.trim() || null,
      readingWrite: rw,
      math: m,
      notes: testForm.notes.trim() || null,
    };
    try {
      if (editingTest) {
        const res = await api.patch(`/sat/tests/${editingTest.id}`, payload);
        setTests((prev) => prev.map((t) => (t.id === editingTest.id ? res.data.test : t))
          .sort((a, b) => new Date(a.takenOn) - new Date(b.takenOn)));
        toast.success('Result updated');
      } else {
        const res = await api.post('/sat/tests', payload);
        setTests((prev) => [...prev, res.data.test].sort((a, b) => new Date(a.takenOn) - new Date(b.takenOn)));
        toast.success('Result logged');
      }
      setShowTest(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save result');
    }
  };

  const deleteTest = async (t) => {
    if (!window.confirm(`Delete the ${format(parseISO(t.takenOn), 'MMM d')} result (${t.total})?`)) return;
    try {
      await api.delete(`/sat/tests/${t.id}`);
      setTests((prev) => prev.filter((x) => x.id !== t.id));
      toast.success('Result deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ---- test dates → deadlines ----
  const addTestDate = async (td) => {
    try {
      await api.post('/deadlines', {
        label: td.label, dueAt: td.date, type: 'OFFICIAL', category: 'SAT', allDay: true,
      });
      if (td.registrationDeadline) {
        try {
          await api.post('/deadlines', {
            label: `Register: ${td.label}`, dueAt: td.registrationDeadline, type: 'PERSONAL', category: 'SAT', allDay: true,
          });
        } catch { /* registration may be a duplicate; ignore */ }
      }
      toast.success(`${td.label} added to deadlines`);
      const res = await api.get('/deadlines');
      setDeadlines(res.data.deadlines || []);
      setClusterWarnings(res.data.clusterWarnings || []);
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to add deadline');
    }
  };

  const addedDateStrings = useMemo(
    () => new Set(satDeadlines.map((d) => format(parseISO(d.dueAt), 'yyyy-MM-dd'))),
    [satDeadlines]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  const hasGoal = plan && (plan.targetTotal || plan.targetTestDate);

  return (
    <div className="max-w-6xl mx-auto animate-fade-in" data-testid="sat-prep-page">
      {/* Header */}
      <div className="border-b border-border pb-5 mb-6 flex items-end justify-between">
        <div>
          <p className="label-swiss mb-1">College Prep</p>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">SAT Prep</h1>
        </div>
        <Button onClick={openCreateTest} size="sm" className="gap-1.5">
          <Plus className="w-4 h-4" /> Log Result
        </Button>
      </div>

      {/* Goal band */}
      {!hasGoal ? (
        <div className="border border-border mb-6 py-10 text-center">
          <Target className="w-6 h-6 text-muted-foreground/50 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground mb-3">Set a target score to anchor your prep</p>
          <Button size="sm" variant="outline" onClick={openGoal} className="gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Set your target
          </Button>
        </div>
      ) : (
        <div className="border border-border mb-6">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <p className="label-swiss">Your Target</p>
            <button onClick={openGoal} className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              <Edit2 className="w-3 h-3" /> Edit goal
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
            <StatCell label="Target Total" value={plan.targetTotal || '—'} accent />
            <StatCell label="R&W" value={plan.targetReadingWrite || '—'} />
            <StatCell label="Math" value={plan.targetMath || '—'} />
            <StatCell label="Test Date" value={plan.targetTestDate ? format(parseISO(plan.targetTestDate), 'MMM d') : '—'} />
            <StatCell
              label="Days Left"
              value={daysLeft == null ? '—' : daysLeft < 0 ? 'Past' : daysLeft}
              className="col-span-2 md:col-span-1"
            />
          </div>
        </div>
      )}

      {/* Progress chart */}
      <div className="border border-border mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="label-swiss">Score Progress</p>
          </div>
          <div className="flex items-center gap-3 text-[10px] font-mono text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: '#4F46E5' }} /> Practice</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: '#16a34a' }} /> Official</span>
          </div>
        </div>
        {tests.length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-muted-foreground mb-2">No results logged yet</p>
            <button onClick={openCreateTest} className="text-xs text-primary font-medium uppercase tracking-wide">
              Log your first test →
            </button>
          </div>
        ) : (
          <div className="px-3 py-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <YAxis domain={yDomain} tick={{ fontSize: 11 }} stroke="#9ca3af" width={48} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  formatter={(value, name) => [value, name === 'total' ? 'Total' : name]}
                />
                {plan?.targetTotal && (
                  <ReferenceLine
                    y={plan.targetTotal}
                    stroke="#4F46E5"
                    strokeDasharray="5 4"
                    label={{ value: `Target ${plan.targetTotal}`, position: 'right', fontSize: 10, fill: '#4F46E5' }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#4F46E5"
                  strokeWidth={2}
                  dot={<ScoreDot />}
                  activeDot={{ r: 6 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {tests.length > 0 && (
          <div className="grid grid-cols-3 divide-x divide-border border-t border-border">
            <StatCell label="Latest" value={latest?.total ?? '—'}
              sub={plan?.targetTotal && latest ? `${latest.total - plan.targetTotal >= 0 ? '+' : ''}${latest.total - plan.targetTotal} vs target` : null} />
            <StatCell label="Best" value={best?.total ?? '—'} />
            <StatCell label="Tests Logged" value={tests.length} />
          </div>
        )}
      </div>

      {/* Practice-test log */}
      <div className="border border-border mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-3.5 h-3.5 text-muted-foreground" />
            <p className="label-swiss">Test Log</p>
          </div>
          <button onClick={openCreateTest} className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>
        {tests.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No results yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  {['Date', 'Type', 'Source', 'R&W', 'Math', 'Total', 'Δ', ''].map((h) => (
                    <th key={h} className="px-4 py-2 label-swiss font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[...tests].reverse().map((t) => {
                  const delta = plan?.targetTotal ? t.total - plan.targetTotal : null;
                  return (
                    <tr key={t.id} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-2.5 font-mono text-[13px] whitespace-nowrap">{format(parseISO(t.takenOn), 'MMM d, yyyy')}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[9px] font-semibold px-1.5 py-0.5 border rounded-sm uppercase tracking-wide ${TEST_TYPE_STYLES[t.testType]}`}>
                          {TEST_TYPE_LABELS[t.testType]}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground max-w-[180px] truncate">{t.source || '—'}</td>
                      <td className="px-4 py-2.5 font-mono">{t.readingWrite}</td>
                      <td className="px-4 py-2.5 font-mono">{t.math}</td>
                      <td className="px-4 py-2.5 font-mono font-bold">{t.total}</td>
                      <td className="px-4 py-2.5 font-mono">
                        {delta == null ? '—' : (
                          <span className={delta >= 0 ? 'text-green-600' : 'text-red-500'}>
                            {delta >= 0 ? '+' : ''}{delta}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1 justify-end">
                          <button onClick={() => openEditTest(t)} title="Edit" className="p-1 rounded hover:bg-muted text-muted-foreground">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => deleteTest(t)} title="Delete" className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Target-college SAT bands */}
        <div className="border border-border">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-3.5 h-3.5 text-muted-foreground" />
              <p className="label-swiss">Target Colleges (SAT range)</p>
            </div>
            <Link to="/college-visits" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
              Colleges <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          {colleges.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground mb-2">No target colleges yet</p>
              <Link to="/college-visits" className="text-xs text-primary font-medium uppercase tracking-wide">Add colleges →</Link>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {colleges.map((c) => (
                <CollegeBand key={c.id} college={c} marker={best?.total ?? null} />
              ))}
              <div className="px-4 py-2 text-[10px] font-mono text-muted-foreground flex items-center justify-between">
                <span>{SCALE_MIN}</span>
                <span>your best ▲</span>
                <span>{SCALE_MAX}</span>
              </div>
            </div>
          )}
        </div>

        {/* SAT dates + prep sessions */}
        <div className="space-y-6">
          {/* Official dates */}
          <div className="border border-border">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="label-swiss">SAT Test Dates</p>
              </div>
              <Link to="/deadlines" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
                Deadlines <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {satWarnings.length > 0 && (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-[11px] text-amber-800 space-y-0.5">
                  {satWarnings.map((w, i) => <p key={i}>{w.message}</p>)}
                </div>
              </div>
            )}
            <div className="divide-y divide-border">
              {testDates.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">No upcoming dates</div>
              )}
              {testDates.map((td) => {
                const added = addedDateStrings.has(td.date);
                return (
                  <div key={td.date} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <p className="text-sm font-medium text-foreground">{td.label}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">
                        {format(parseISO(td.date), 'EEE MMM d, yyyy')}
                        {td.registrationDeadline && ` · reg by ${format(parseISO(td.registrationDeadline), 'MMM d')}`}
                      </p>
                    </div>
                    {added ? (
                      <span className="text-[10px] font-semibold text-green-600 flex items-center gap-1 shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Added
                      </span>
                    ) : (
                      <button onClick={() => addTestDate(td)} className="text-[11px] text-primary font-medium uppercase tracking-wide shrink-0">
                        + Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Prep sessions (manual) */}
          <div className="border border-border">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-3.5 h-3.5 text-muted-foreground" />
                <p className="label-swiss">Prep Sessions</p>
              </div>
              <Link to="/planner" className="text-[11px] text-primary font-medium flex items-center gap-1 uppercase tracking-wide">
                Planner <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {satSessions.length === 0 ? (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">No SAT sessions scheduled</p>
                <Link to="/planner" className="text-xs text-primary font-medium uppercase tracking-wide">
                  Schedule in planner →
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-border max-h-64 overflow-y-auto">
                {satSessions.map((s) => (
                  <Link key={s.id} to="/planner" className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/20 group">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate group-hover:text-primary">{s.title}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">{format(parseISO(s.scheduledAt), 'EEE MMM d · HH:mm')}</p>
                    </div>
                    <span className="text-[9px] font-semibold px-1.5 py-0.5 border rounded-sm uppercase tracking-wide bg-primary/5 text-primary border-primary/20 shrink-0">
                      {SAT_SECTION_LABELS[s.satSection] || 'SAT'}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Goal Dialog */}
      <Dialog open={showGoal} onOpenChange={(o) => { if (!o) setShowGoal(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base font-bold">SAT Target</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <button
              onClick={handleSuggest}
              disabled={suggesting}
              className="w-full flex items-center justify-center gap-2 border border-primary/30 bg-primary/5 text-primary text-sm font-medium py-2 rounded hover:bg-primary/10 transition-colors disabled:opacity-60"
            >
              <Sparkles className="w-3.5 h-3.5" /> {suggesting ? 'Calculating…' : 'Suggest from my colleges'}
            </button>

            {suggestion && suggestion.suggestedTotal != null && (
              <div className="border border-border bg-muted/40 rounded p-3 text-[11px] space-y-1.5">
                <p className="text-foreground">
                  Aim for <span className="font-bold font-mono">{suggestion.suggestedTotal}</span> — the 75th-percentile of your most selective target.
                </p>
                <div className="space-y-0.5">
                  {suggestion.basis.slice(0, 5).map((b) => (
                    <div key={b.collegeName} className="flex justify-between text-muted-foreground">
                      <span className="truncate mr-2">{b.collegeName}</span>
                      <span className="font-mono shrink-0">{b.composite75}</span>
                    </div>
                  ))}
                </div>
                {suggestion.collegesMissingSat > 0 && (
                  <p className="text-amber-600">{suggestion.collegesMissingSat} college(s) had no SAT data (often test-optional).</p>
                )}
              </div>
            )}
            {suggestion && suggestion.suggestedTotal == null && (
              <p className="text-[11px] text-amber-600">{suggestion.message}</p>
            )}

            <div className="grid grid-cols-3 gap-3">
              <NumberField label="Total" value={goalForm.targetTotal} onChange={(v) => setGoalForm((f) => ({ ...f, targetTotal: v }))} placeholder="1500" />
              <NumberField label="R&W" value={goalForm.targetReadingWrite} onChange={(v) => setGoalForm((f) => ({ ...f, targetReadingWrite: v }))} placeholder="750" />
              <NumberField label="Math" value={goalForm.targetMath} onChange={(v) => setGoalForm((f) => ({ ...f, targetMath: v }))} placeholder="750" />
            </div>
            <div>
              <label className="label-swiss block mb-1.5">Target Test Date</label>
              <input
                type="date"
                className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                value={goalForm.targetTestDate}
                onChange={(e) => setGoalForm((f) => ({ ...f, targetTestDate: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowGoal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveGoal}>Save Target</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={showTest} onOpenChange={(o) => { if (!o) setShowTest(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">{editingTest ? 'Edit Result' : 'Log Test Result'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-swiss block mb-1.5">Date *</label>
                <input
                  type="date"
                  className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  value={testForm.takenOn}
                  onChange={(e) => setTestForm((f) => ({ ...f, takenOn: e.target.value }))}
                />
              </div>
              <div>
                <label className="label-swiss block mb-1.5">Type</label>
                <select
                  className="w-full border border-border rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                  value={testForm.testType}
                  onChange={(e) => setTestForm((f) => ({ ...f, testType: e.target.value }))}
                >
                  {TEST_TYPES.map((t) => <option key={t} value={t}>{TEST_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="label-swiss block mb-1.5">Source</label>
              <input
                type="text"
                className="w-full border border-border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="e.g. Bluebook Practice #4"
                value={testForm.source}
                onChange={(e) => setTestForm((f) => ({ ...f, source: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 items-end">
              <NumberField label="R&W *" value={testForm.readingWrite} onChange={(v) => setTestForm((f) => ({ ...f, readingWrite: v }))} placeholder="700" />
              <NumberField label="Math *" value={testForm.math} onChange={(v) => setTestForm((f) => ({ ...f, math: v }))} placeholder="700" />
              <div className="text-center pb-1">
                <p className="label-swiss mb-1.5">Total</p>
                <p className="text-2xl font-bold font-mono tracking-tight">
                  {testForm.readingWrite && testForm.math ? Number(testForm.readingWrite) + Number(testForm.math) : '—'}
                </p>
              </div>
            </div>
            <div>
              <label className="label-swiss block mb-1.5">Notes</label>
              <textarea
                rows={2}
                className="w-full border border-border rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                placeholder="e.g. ran out of time on the last math module"
                value={testForm.notes}
                onChange={(e) => setTestForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowTest(false)}>Cancel</Button>
            <Button size="sm" onClick={saveTest}>{editingTest ? 'Save Changes' : 'Log Result'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Custom dot: official results stand out (larger, green)
function ScoreDot({ cx, cy, payload }) {
  if (cx == null || cy == null) return null;
  const official = payload?.type === 'OFFICIAL';
  return (
    <circle
      cx={cx}
      cy={cy}
      r={official ? 6 : 4}
      fill={official ? '#16a34a' : '#4F46E5'}
      stroke="#fff"
      strokeWidth={1.5}
    />
  );
}

function CollegeBand({ college, marker }) {
  const c25 = college.sat25 ?? null;
  const c75 = college.sat75 ?? null;
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2 mb-1.5">
        <CollegeLogo schoolUrl={college.schoolUrl} name={college.collegeName} size={20} />
        <p className="text-sm font-medium text-foreground truncate flex-1">{college.collegeName}</p>
        {c25 != null && c75 != null ? (
          <span className="text-[10px] font-mono text-muted-foreground shrink-0">{c25}–{c75}</span>
        ) : (
          <span className="text-[10px] text-muted-foreground shrink-0">no SAT data</span>
        )}
      </div>
      <div className="relative h-2 bg-muted rounded-full">
        {c25 != null && c75 != null && (
          <div
            className="absolute h-2 bg-primary/30 rounded-full"
            style={{ left: `${scalePct(c25)}%`, width: `${Math.max(2, scalePct(c75) - scalePct(c25))}%` }}
          />
        )}
        {marker != null && (
          <div
            className="absolute -top-1 w-0 h-0 border-l-4 border-r-4 border-t-[6px] border-l-transparent border-r-transparent border-t-foreground"
            style={{ left: `calc(${scalePct(marker)}% - 4px)` }}
            title={`Your best: ${marker}`}
          />
        )}
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="label-swiss block mb-1.5">{label}</label>
      <input
        type="number"
        className="w-full border border-border rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function StatCell({ label, value, sub, className = '', accent = false }) {
  return (
    <div className={`py-5 px-5 ${className}`}>
      <p className={`text-2xl font-bold font-mono tracking-tight ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</p>
      <p className="label-swiss mt-1">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
